import { AppState } from './app-state'
import { IAppRoute } from './app/router-interface'


export default class AppRoutes {
    static readonly eventInteraction: IAppRoute = {
        name: 'event-interactions',
        path: '/event-interactions/:eventId',
        component: (() => import('./pages/event-interactions'))
    }

    static readonly eventInteractionQuestionBroadcast: IAppRoute = {
        name: 'event-interaction-broadcast',
        path: '/event-interaction-broadcast/:eventId',
        component: (() => import('./pages/event-interactions/broadcast')),
        fullScreen: true
    }

    static readonly eventInteractionQuestionDisplay: IAppRoute = {
        name: 'event-interaction-display',
        path: '/event-interaction-display/:eventId',
        component: (() => import('./pages/event-interactions/display')),
        fullScreen: true
    }

    static readonly mobileAppNewsfeedEditor: IAppRoute = {
        name: 'newsfeed-editor',
        path: '/newsfeed-editor/:eventId',
        component: (() => import('./pages/mobile-app/newsfeed-editor'))
    }

    static readonly accountingData: IAppRoute = {
        name: 'accounting-data',
        path: '/accounting-data/:id',
        component: (() => import('./pages/admin/accounting-data')),
        customLayout: true
    }

    static readonly adminPreassignedTickets: IAppRoute = {
        name: 'admin-preassigned-tickets',
        path: '/admin/preassignedtickets/:eventId',
        component: (() => import('./pages/admin/preassigned-tickets')),
        customLayout: true
    }

    static readonly adminTicketsPreAssignedIds: IAppRoute = {
        name: 'admin-ticket-preassigned-ids',
        path: '/admin/ticket-preassigned-ids/:eventId',
        component: (() => import('./pages/admin/ticket-preassigned-ids')),
        customLayout: true
    }

    static readonly adminAlternativeCheckingIds: IAppRoute = {
        name: 'admin-alternative-checking-ids',
        path: '/admin/alternative-checking-ids/:eventId',
        component: (() => import('./pages/admin/alternative-checking-ids')),
        customLayout: true
    }

    static readonly adminTicketNumberAssignment: IAppRoute = {
        name: 'admin-tn-assignment',
        path: '/admin/ticketnumberassignment',
        component: (() => import('./pages/admin/ticket-number-assignment')),
        customLayout: true
    }

    static readonly adminUserLogin: IAppRoute = {
        name: 'admin-user-login',
        path: '/admin/userLogin',
        component: (() => import('./pages/admin/user-login')),
        customLayout: true
    }

    static readonly adminEventStreamSessions: IAppRoute = {
        name: 'admin-eventstream-sessions',
        path: '/admin/event-stream-sessions/:eventId',
        component: (() => import('./pages/admin/event-stream-session')),
        customLayout: true
    }

    static readonly adminEventDeltaLog: IAppRoute = {
        name: 'admin-delta-log',
        path: '/admin/delta-log/:eventId',
        component: (() => import('./pages/admin/delta-log')),
        customLayout: true
    }

    static readonly adminBankPaymentGenerator: IAppRoute = {
        name: 'admin-bank-payment-generator',
        path: '/admin/bank-payment-generator',
        component: (() => import('./pages/admin/bank-payment-generator')),
        customLayout: true
    }

    static readonly adminEraseEventRsvp: IAppRoute = {
        name: 'admin-erase-event-rsvp',
        path: '/admin/erase-event-rsvp',
        component: (() => import('./pages/admin/erase-event-rsvp')),
        customLayout: true
    }

    static readonly adminAclManagement: IAppRoute = {
        name: 'admin-acl-management',
        path: '/admin/acl-management',
        component: (() => import('./pages/admin/acl-management')),
        customLayout: true
    }

    static readonly adminNcziExport: IAppRoute = {
        name: 'nczi',
        path: '/admin/nczi',
        title: (() => 'NCZI export'),
        component: (() => import('./pages/admin/nczi')),
        customLayout: true
    }

    static readonly marketplacePayments: IAppRoute = {
        name: 'marketplace-payments',
        path: '/admin/marketplace-payments',
        title: (() => "Marketplace payments"),
        component: (() => import('./pages/admin/marketplace-payments')),
        customLayout: true
    }

    static readonly widgets: IAppRoute = {
        name: 'event-widgets',
        path: '/event-widgets/:eventId',
        title: (() => AppState.resources.widgets),
        customLayout: true,
        component: (() => import('./pages/event-management/widgets'))
    }

    static readonly editProfile: IAppRoute = {
        name: 'edit-profile',
        path: '/edit-profile',
        fullScreen: true,
        layout: 'buyer',
        title: (() => AppState.resources.editProfile),
        component: (() => import('./pages/account/edit-profile'))
    }

    static readonly editProfileNew: IAppRoute = {
        name: 'edit-profile-new',
        path: '/edit-profile-new',
        fullScreen: false,
        title: (() => AppState.resources.editProfile),
        component: (() => import('./pages/account/edit-profile'))
    }

    static readonly attendeeList: IAppRoute = {
        name: 'attendee-list',
        path: '/attendee-list/:eventId',
        alias: ['/attendee-list/ticket/:eventId'], //has to be "eventId" due to other automations
        title: (() => AppState.resources.attendeeListTitle),
        customLayout: true,
        component: (() => import('./pages/event-management/attendee-list'))
    }

    static readonly promoCodes: IAppRoute = {
        name: 'promo-codes',
        path: '/promo-codes/:eventId',
        title: (() => AppState.resources.promoCodeTitle),
        customLayout: true,
        component: (() => import('./pages/event-management/promo-codes'))
    }

    static readonly guestlistManagement: IAppRoute = {
        name: 'guestlist-management',
        path: '/guestlist-management/:eventId',
        title: (() => AppState.resources.guestlist),
        customLayout: true,
        component: (() => import('./pages/guestlist/guestlist-management'))
    }

    static readonly guestlistInvitation: IAppRoute = {
        name: 'guestlist-invitation',
        path: '/guestlist-invite/:token',
        fullScreen: true,
        title: (() => AppState.resources.guestlist),
        component: (() => import('./pages/guestlist/guestlist-invitation'))
    }

    static readonly rsvpEvent: IAppRoute = {
        name: 'rsvp-event',
        path: '/rsvp-event*',
        fullScreen: true,
        title: (() => AppState.resources.guestlist),
        component: (() => import('./pages/guestlist/guestlist-invitation'))
    }

    static readonly eventRsvp: IAppRoute = {
        name: 'event-rsvp',
        path: '/event-invitations/:eventId',
        title: (() => 'RSVP'),
        customLayout: true,
        component: (() => import('./pages/event-management/rsvp'))
    }

    static readonly statsPersonal: IAppRoute = {
        name: 'stats-personal',
        path: '/visitor-stats',
        title: (() => AppState.resources.statsPersonal),
        customLayout: true,
        component: (() => import('./pages/stats/stats-personal'))
    }

    static readonly statsPersonalDynamic: IAppRoute = {
        name: 'stats-personal-dynamic',
        path: '/stats-personal-dynamic',
        title: (() => AppState.resources.statsPersonal),
        customLayout: true,
        component: (() => import('./pages/stats/stats-personal-dynamic'))
    }

    static readonly statsFinancial: IAppRoute = {
        name: 'stats-financial',
        path: '/financial-stats',
        title: (() => AppState.resources.statsFinancialCaption),
        component: (() => import('./pages/stats/stats-financial'))
    }

    static readonly deviceManager: IAppRoute = {
        name: 'device-manager',
        path: '/device-manager',
        title: (() => 'Device manager'),
        component: (() => import('./pages/access-control/device-manager'))
    }

    static readonly eventManagementSelection: IAppRoute = {
        name: 'event-management',
        path: '/event-management',
        title: (() => AppState.resources.eventManagementTitle),
        customLayout: true,
        component: (() => import('./pages/event-management/event-management-selection'))
    }

    static readonly eventOrders: IAppRoute = {
        name: 'event-orders',
        path: '/event-orders/:eventId',
        title: (() => AppState.resources.eventOrdersTitle),
        customLayout: true,
        component: (() => import('./pages/event-management/event-orders'))
    }

    static readonly importAttendees: IAppRoute = {
        name: 'import-attendees',
        path: '/import-attendees/:eventId',
        title: (() => AppState.resources.importTitle),
        customLayout: true,
        component: (() => import('./pages/event-management/import-attendees'))
    }

    static readonly eventDashboard: IAppRoute = {
        name: 'event-dashboard',
        path: '/event-dashboard/:eventId',
        title: (() => AppState.resources.eventDashboardTitle),
        customLayout: true,
        component: (() => import('./pages/event-management/event-dashboard'))
    }

    static readonly editEvent: IAppRoute = {
        name: 'edit-event',
        path: '/edit-event/:eventId',
        title: (() => AppState.resources.editEventCaption),
        fullScreen: false,
        customLayout: true,
        component: (() => import('./pages/event-management/edit-event'))
    }

    static readonly createEvent: IAppRoute = {
        name: 'create-event',
        path: '/create-event',
        title: (() => AppState.resources.createNewEventCaption),
        fullScreen: false,
        customLayout: true,
        component: (() => import('./pages/event-management/edit-event'))
    }

    static readonly createEventBackOffice: IAppRoute = {
        name: 'create-event-bo',
        path: '/create-event-bo',
        title: (() => AppState.resources.createNewEventCaption),
        fullScreen: false,
        customLayout: true,
        component: (() => import('./pages/event-management/edit-event'))
    }

    static readonly posSale: IAppRoute = {
        name: 'pos-sale',
        path: '/pos-sale',
        title: (() => AppState.resources.posSaleTitle),
        customLayout: true,
        component: (() => import('./pages/access-control/pos-sale'))
    }

    static readonly posSaleAcl: IAppRoute = {
        name: 'pos-sale-acl',
        path: '/pos-sale-acl/:aclItemId',
        title: (() => AppState.resources.posSaleTitle),
        customLayout: true,
        component: (() => import('./pages/access-control/pos-sale'))
    }

    static readonly accessControlAttendees: IAppRoute = {
        name: 'acl-attendees',
        path: '/acl-attendees/:aclItemId',
        title: (() => AppState.resources.accessControlAttendees),
        customLayout: true,
        component: (() => import('./pages/access-control/acl-attendees'))
    }

    static readonly accessControlCheckingSelection: IAppRoute = {
        name: 'acl-checking-selection',
        path: '/acl-checking-selection',
        title: (() => AppState.resources.accessControlCheckingSelection),
        customLayout: true,
        component: (() => import('./pages/access-control/acl-checking-selection'))
    }

    /**
     * Modernized ACL configuration page (replacement for legacy /access-control Razor view).
     * Two routes share one component:
     *   - /access-control-new          → drilldown (groups + items)
     *   - /access-control-new/:itemId  → item-details editor
     */
    static readonly accessControlNew: IAppRoute = {
        name: 'access-control-new',
        path: '/access-control-new',
        title: (() => 'Access control'),
        customLayout: true,
        component: (() => import('./pages/access-control/access-control-new'))
    }

    static readonly accessControlNewDetail: IAppRoute = {
        name: 'access-control-new-detail',
        path: '/access-control-new/:itemId',
        title: (() => 'Access control'),
        customLayout: true,
        component: (() => import('./pages/access-control/access-control-new'))
    }

    static readonly accessControlConfigOverview: IAppRoute = {
        name: 'acl-config-overview',
        path: '/acl-config-overview',
        title: (() => 'ACL config overview'),
        component: (() => import('./pages/access-control/acl-config-overview'))
    }

    static readonly accessControlTicketChecking: IAppRoute = {
        name: 'acl-ticket-check',
        path: '/acl-ticket-check/:aclItemId',
        title: (() => AppState.resources.menuAccessControl),
        customLayout: true,
        component: (() => import('./pages/access-control/acl-ticket-check'))
    }

    static readonly accessControlTicketEdit: IAppRoute = {
        name: 'ticket-edit',
        path: '/ticket-edit',
        title: (() => AppState.resources.editTicketNumber),
        component: (() => import('./pages/access-control/ticket-edit'))
    }

    static readonly accessControlLiveFeed: IAppRoute = {
        name: 'acl-live-feed',
        path: '/acl-live-feed/:aclItemId',
        title: (() => "Live Feed"),
        customLayout: true,
        component: (() => import('./pages/access-control/acl-live-feed'))
    }

    static readonly eventSchedule: IAppRoute = {
        name: 'event-schedule',
        path: '/event-schedule/:eventId',
        title: (() => AppState.resources.eventSchedule),
        customLayout: true,
        component: (() => import('./pages/event-schedule/schedule-list'))
    }

    static readonly eventScheduleDisplay: IAppRoute = {
        name: 'event-schedule-display',
        path: '/event-schedule-display/:eventId',
        title: (() => AppState.resources.eventSchedule),
        component: (() => import('./pages/event-schedule/event-schedule-display'))
    }

    static readonly eventScheduleGrid: IAppRoute = {
        name: 'event-schedule-grid',
        path: '/event-schedule-grid/:eventId',
        title: (() => AppState.resources.eventSchedule),
        customLayout: true,
        component: (() => import('./pages/event-schedule/schedule-grid-page'))
    }

    static readonly eventScheduleVenue: IAppRoute = {
        name: 'event-schedule-venues',
        path: '/event-schedule-venues/:eventId',
        title: (() => AppState.resources.eventScheduleVenues),
        customLayout: true,
        component: (() => import('./pages/event-schedule/schedule-venue-list'))
    }

    static readonly eventScheduleChanges: IAppRoute = {
        name: 'event-schedule-changes',
        path: '/event-schedule-changes/:eventId',
        title: (() => 'Schedule changes'),
        customLayout: true,
        component: (() => import('./pages/event-schedule/schedule-changes'))
    }

    static readonly eventExhibitors: IAppRoute = {
        name: 'event-exhibitors',
        path: '/event-exhibitors/:eventId',
        title: (() => AppState.resources.eventScheduleExhibitorsTitle),
        customLayout: true,
        component: (() => import('./pages/event-schedule/event-exhibitors-list'))
    }

    static readonly eventExhibitorsDisplay: IAppRoute = {
        name: 'event-exhibitors-display',
        path: '/event-exhibitors-display/:eventId',
        title: (() => AppState.resources.eventScheduleExhibitorsTitle),
        component: (() => import('./pages/event-schedule/event-exhibitors-display'))
    }

    static readonly myTickets: IAppRoute = {
        name: 'my-tickets-events',
        path: '/my-tickets',
        title: (() => "My tickets"),
        component: (() => import('./pages/event-display/my-ticket-events')),
        fullScreen: true,
        layout: 'buyer'
    }

    static readonly myTicketsList: IAppRoute = {
        name: 'my-tickets',
        path: '/my-tickets/:ticketsIdentifier',
        title: (() => "My tickets"),
        component: (() => import('./pages/event-display/my-tickets')),
        fullScreen: true,
        layout: 'buyer'
    }

    static readonly marketplace: IAppRoute = {
        name: 'marketplace',
        path: '/marketplace',
        title: (() => "Marketplace"),
        component: (() => import('./pages/event-display/marketplace')),
        fullScreen: true,
        layout: 'buyer'
    }

    static readonly marketplaceEvent: IAppRoute = {
        name: 'marketplace-event',
        path: '/marketplace-event/:ticketsIdentifier',
        title: (() => "Marketplace event"),
        component: (() => import('./pages/event-display/marketplace-event')),
        fullScreen: true,
        layout: 'marketplace'
    }

    //Test pages x2 - using @powerduck components
    static readonly counter: IAppRoute = {
        name: 'counter',
        path: '/testspa/counter',
        title: (() => 'Test counter'),
        component: (() => import('@powerduck/components/counter/index'))
    }

    static readonly fetchData: IAppRoute = {
        name: 'fetchData',
        path: '/testspa/fetchdata',
        component: (() => import('@powerduck/components/counter/fetchdata'))
    }

    static readonly wysiwig: IAppRoute = {
        name: 'wysiwig',
        path: '/testspa/wysiwig',
        component: (() => import('@powerduck/components/input/wysiwig'))
    }

    static readonly createbyimport: IAppRoute = {
        name: 'createbyimport',
        path: '/testspa/createbyimport',
        title: (() => 'Create simple event'),
        component: (() => import('./pages/event-management/simpleeventcreator'))
    }

    static readonly customerSpecificPapezSupport: IAppRoute = {
        name: 'customerSpecificPapezSupport',
        path: '/customer-specific/np-support',
        title: (() => 'NP Support'),
        component: (() => import('./pages/customer-specific/outside-admin/index'))
    }

    static readonly cssSandbox: IAppRoute = {
        name: 'cssSandbox',
        path: '/css-sandbox',
        title: (() => 'CSS Sandbox'),
        component: (() => import('./pages/css-sandbox'))
    }

    static readonly templateDesignerTest: IAppRoute = {
        name: 'templateDesignerTest',
        path: '/template-designer-test',
        title: (() => 'Template designer — sandbox'),
        component: (() => import('./pages/template-designer-test'))
    }

    // CRM module — cross-event customer & analytics hub.
    static readonly crmDashboard: IAppRoute = {
        name: 'crm-dashboard',
        path: '/crm',
        title: (() => 'CRM'),
        customLayout: true,
        component: (() => import('./pages/crm'))
    }

    static readonly crmCustomers: IAppRoute = {
        name: 'crm-customers',
        path: '/crm/customers',
        title: (() => 'CRM — Zákazníci'),
        customLayout: true,
        component: (() => import('./pages/crm/customers'))
    }

    static readonly crmCustomerDetail: IAppRoute = {
        name: 'crm-customer-detail',
        path: '/crm/customers/:emailKey',
        title: (() => 'CRM — Zákazník'),
        customLayout: true,
        component: (() => import('./pages/crm/customer-detail'))
    }

    static readonly crmSegments: IAppRoute = {
        name: 'crm-segments',
        path: '/crm/segments',
        title: (() => 'CRM — Segmenty'),
        customLayout: true,
        component: (() => import('./pages/crm/segments'))
    }

    static readonly crmSegmentDetail: IAppRoute = {
        name: 'crm-segment-detail',
        path: '/crm/segments/:segmentId',
        title: (() => 'CRM — Segment'),
        customLayout: true,
        component: (() => import('./pages/crm/segments/segment-detail'))
    }

    static readonly crmSales: IAppRoute = {
        name: 'crm-sales',
        path: '/crm/sales',
        title: (() => 'CRM — Predaj'),
        customLayout: true,
        component: (() => import('./pages/crm/sales'))
    }

    static readonly crmEvents: IAppRoute = {
        name: 'crm-events',
        path: '/crm/events',
        title: (() => 'CRM — Eventy'),
        customLayout: true,
        component: (() => import('./pages/crm/events'))
    }

    static readonly crmOperations: IAppRoute = {
        name: 'crm-operations',
        path: '/crm/operations',
        title: (() => 'CRM — Prevádzka'),
        customLayout: true,
        component: (() => import('./pages/crm/operations'))
    }


    static readonly crmRetention: IAppRoute = {
        name: 'crm-retention',
        path: '/crm/retention',
        title: (() => 'CRM — Retencia'),
        customLayout: true,
        component: (() => import('./pages/crm/retention'))
    }

    static readonly crmAlerts: IAppRoute = {
        name: 'crm-alerts',
        path: '/crm/alerts',
        title: (() => 'CRM — Alerty'),
        customLayout: true,
        component: (() => import('./pages/crm/alerts'))
    }

    static readonly crmReports: IAppRoute = {
        name: 'crm-reports',
        path: '/crm/reports',
        title: (() => 'CRM — Reporty'),
        customLayout: true,
        component: (() => import('./pages/crm/reports'))
    }

    static readonly crmReportBuilderNew: IAppRoute = {
        name: 'crm-report-new',
        path: '/crm/reports/new',
        title: (() => 'CRM — Nový report'),
        customLayout: true,
        component: (() => import('./pages/crm/reports/report-builder'))
    }

    static readonly crmReportDetail: IAppRoute = {
        name: 'crm-report-detail',
        path: '/crm/reports/:reportId',
        title: (() => 'CRM — Report'),
        customLayout: true,
        component: (() => import('./pages/crm/reports/report-builder'))
    }

    static readonly crmReportEdit: IAppRoute = {
        name: 'crm-report-edit',
        path: '/crm/reports/:reportId/edit',
        title: (() => 'CRM — Editovať report'),
        customLayout: true,
        component: (() => import('./pages/crm/reports/report-builder'))
    }

    static readonly crmReportShared: IAppRoute = {
        name: 'crm-report-shared',
        path: '/crm/reports/shared/:token',
        title: (() => 'CRM — Zdieľaný report'),
        customLayout: true,
        component: (() => import('./pages/crm/reports/shared-report'))
    }

    static readonly crmPlayground: IAppRoute = {
        name: 'crm-playground',
        path: '/crm/playground',
        title: (() => 'CRM — Component playground'),
        customLayout: true,
        component: (() => import('./pages/crm/playground'))
    }
}
